import axios from 'axios';
import { getAuth } from 'firebase/auth';

// Configuración base para las peticiones al API según el entorno
const API_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5002'
  : (process.env.REACT_APP_API_EDU_URL || 'https://ggtvp8hnyb.execute-api.us-east-2.amazonaws.com/prod');

// Modificar la configuración de axios
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 300000 // Timeout
});

// Interceptor para añadir el token de autenticación a cada petición
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Obtener el token actual directamente de Firebase Auth
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (currentUser) {
        // Obtener el token fresco para cada petición
        const token = await currentUser.getIdToken(true);
        config.headers['Authorization'] = `Bearer ${token}`;
        console.log('Token de autenticación añadido a la petición');
      } else {
        console.warn('No hay usuario autenticado en Firebase');
      }
    } catch (error) {
      console.error('Error al obtener token de autenticación:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Servicio para comunicación con el backend de AGT Edu
 */
const agtEduService = {
  /**
   * Obtiene todas las asignaturas del usuario
   * @returns {Promise<Object>} Respuesta con las asignaturas
   */
  getSubjects: async () => {
    try {
      console.log('URL base:', apiClient.defaults.baseURL);
      console.log('URL completa:', apiClient.getUri({ url: '/subjects' }));
      const response = await apiClient.get('/subjects');
      return response.data;
    } catch (error) {
      console.error('Error al obtener asignaturas:', error);
      throw error;
    }
  },

  /**
   * Crea una nueva asignatura
   * @param {Object} subjectData Datos de la asignatura
   * @returns {Promise<Object>} Respuesta con la asignatura creada
   */
  createSubject: async (subjectData) => {
    try {
      const response = await apiClient.post('/subjects', subjectData);
      return response.data;
    } catch (error) {
      console.error('Error al crear asignatura:', error);
      throw error;
    }
  },

  /**
   * Actualiza una asignatura existente
   * @param {string} subjectId ID de la asignatura
   * @param {Object} subjectData Datos a actualizar
   * @returns {Promise<Object>} Respuesta con la asignatura actualizada
   */
  updateSubject: async (subjectId, subjectData) => {
    try {
      const response = await apiClient.put(`/subjects/${subjectId}`, subjectData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar asignatura:', error);
      throw error;
    }
  },

  /**
   * Elimina una asignatura
   * @param {string} subjectId ID de la asignatura
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  deleteSubject: async (subjectId) => {
    try {
      const response = await apiClient.delete(`/subjects/${subjectId}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar asignatura:', error);
      throw error;
    }
  },

  /**
   * Obtiene los archivos de una asignatura
   * @param {string} subjectId ID de la asignatura
   * @returns {Promise<Object>} Respuesta con los archivos
   */
  getSubjectFiles: async (subjectId) => {
    try {
      const response = await apiClient.get(`/subjects/${subjectId}/files`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener archivos de la asignatura:', error);
      throw error;
    }
  },

  /**
   * Crea un nuevo archivo
   * @param {Object} fileData Datos del archivo
   * @returns {Promise<Object>} Respuesta con el archivo creado
   */
  createFile: async (fileData) => {
    try {
      const response = await apiClient.post('/files', fileData);
      return response.data;
    } catch (error) {
      console.error('Error al crear archivo:', error);
      throw error;
    }
  },

  /**
   * Elimina un archivo
   * @param {string} fileId ID del archivo
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  deleteFile: async (fileId) => {
    try {
      const response = await apiClient.delete(`/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar archivo:', error);
      throw error;
    }
  },

  /**
   * Envía un mensaje y obtiene la respuesta del asistente
   * @param {Object} messageData Datos del mensaje
   * @param {string} messageData.text Texto del mensaje
   * @param {string} messageData.subjectId ID de la asignatura (opcional)
   * @param {string} messageData.conversationId ID de la conversación (opcional)
   * @param {boolean} messageData.useDeepResearch Si es true, utiliza la investigación profunda
   * @param {Array} messageData.fileIds IDs de archivos adjuntos
   * @returns {Promise<Object>} Respuesta con el mensaje y la respuesta del asistente
   */
  sendMessage: async (messageData) => {
    try {
      // Registrar si se usa Deep Research
      if (messageData.useDeepResearch) {
        console.log('Enviando mensaje con Deep Research activado');
      }
      
      const response = await apiClient.post('/messages', messageData);
      return response.data;
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      throw error;
    }
  },

  /**
   * Obtiene los mensajes de una asignatura
   * @param {string} subjectId ID de la asignatura
   * @param {number} limit Número máximo de mensajes a obtener
   * @returns {Promise<Object>} Respuesta con los mensajes
   */
  getSubjectMessages: async (subjectId, limit = 50) => {
    try {
      const response = await apiClient.get(`/messages/subject/${subjectId}?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener mensajes de la asignatura:', error);
      throw error;
    }
  },

  /**
   * Obtiene los mensajes de una conversación
   * @param {string} conversationId ID de la conversación
   * @param {number} limit Número máximo de mensajes a obtener
   * @returns {Promise<Object>} Respuesta con los mensajes
   */
  getConversationMessages: async (conversationId, limit = 50) => {
    try {
      const response = await apiClient.get(`/conversations/${conversationId}/messages?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener mensajes de la conversación:', error);
      throw error;
    }
  },

  /**
   * Obtiene todas las conversaciones del usuario
   * @returns {Promise<Object>} Respuesta con las conversaciones
   */
  getConversations: async () => {
    try {
      const response = await apiClient.get('/conversations');
      return response.data;
    } catch (error) {
      console.error('Error al obtener conversaciones:', error);
      throw error;
    }
  },

  /**
   * Crea una nueva conversación
   * @param {Object} conversationData Datos de la conversación
   * @param {string} conversationData.title Título de la conversación
   * @param {string} conversationData.subjectId ID de la asignatura (opcional)
   * @param {string} conversationData.lastMessage Último mensaje (opcional)
   * @returns {Promise<Object>} Respuesta con la conversación creada
   */
  createConversation: async (conversationData) => {
    try {
      const response = await apiClient.post('/conversations', conversationData);
      return response.data;
    } catch (error) {
      console.error('Error al crear conversación:', error);
      throw error;
    }
  },

  /**
   * Actualiza una conversación existente
   * @param {string} conversationId ID de la conversación
   * @param {Object} conversationData Datos a actualizar
   * @returns {Promise<Object>} Respuesta con la conversación actualizada
   */
  updateConversation: async (conversationId, conversationData) => {
    try {
      const response = await apiClient.put(`/conversations/${conversationId}`, conversationData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar conversación:', error);
      throw error;
    }
  },

  /**
   * Elimina una conversación
   * @param {string} conversationId ID de la conversación
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  deleteConversation: async (conversationId) => {
    try {
      const response = await apiClient.delete(`/conversations/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar conversación:', error);
      throw error;
    }
  },

  /**
   * Sube un archivo a una asignatura
   * @param {string} subjectId ID de la asignatura
   * @param {FormData} formData Datos del formulario con el archivo
   * @param {Function} onProgress Función para seguimiento del progreso
   * @returns {Promise<Object>} Respuesta con información del archivo subido
   */
  uploadFile: async (subjectId, formData, onProgress) => {
    try {
      const response = await apiClient.post(`/files/upload/${subjectId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress ? 
          progressEvent => onProgress(progressEvent) : 
          undefined
      });
      return response.data;
    } catch (error) {
      console.error('Error al subir archivo:', error);
      throw error;
    }
  },

  /**
   * Sube múltiples archivos a una asignatura
   * @param {string} subjectId ID de la asignatura
   * @param {FormData} formData Datos del formulario con los archivos
   * @param {Function} onProgress Función para seguimiento del progreso
   * @returns {Promise<Object>} Respuesta con información de los archivos subidos
   */
  uploadMultipleFiles: async (subjectId, formData, onProgress) => {
    try {
      const response = await apiClient.post(`/files/multi-upload/${subjectId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress ? 
          progressEvent => onProgress(progressEvent) : 
          undefined
      });
      return response.data;
    } catch (error) {
      console.error('Error al subir múltiples archivos:', error);
      throw error;
    }
  },

  /**
   * Descarga un archivo
   * @param {string} fileId ID del archivo
   * @returns {Promise<Blob>} Blob con el contenido del archivo
   */
  downloadFile: async (fileId) => {
    try {
      const response = await apiClient.get(`/files/download/${fileId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error al descargar archivo:', error);
      throw error;
    }
  },

  /**
   * Refresca la URL de acceso a un archivo
   * @param {string} fileId ID del archivo
   * @param {number} expiry Tiempo de expiración en segundos
   * @returns {Promise<Object>} Respuesta con la URL actualizada
   */
  refreshFileUrl: async (fileId, expiry = 3600) => {
    try {
      const response = await apiClient.get(`/files/refresh/${fileId}?expiry=${expiry}`);
      return response.data;
    } catch (error) {
      console.error('Error al refrescar URL de archivo:', error);
      throw error;
    }
  },

  /**
   * Sube archivos individuales sin asociarlos a una asignatura
   * @param {FormData} formData Datos del formulario con los archivos
   * @param {Function} onProgress Función para seguimiento del progreso
   * @returns {Promise<Object>} Respuesta con información de los archivos subidos
   */
  uploadIndividualFiles: async (formData, onProgress) => {
    try {
      const response = await apiClient.post('/files/individual-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress ? 
          progressEvent => onProgress(progressEvent) : 
          undefined
      });
      return response.data;
    } catch (error) {
      console.error('Error al subir archivos individuales:', error);
      throw error;
    }
  },

  /**
   * Obtiene los archivos individuales del usuario (sin asignatura)
   * @returns {Promise<Object>} Respuesta con los archivos
   */
  getIndividualFiles: async () => {
    try {
      // Añadir un timestamp para evitar la caché
      const timestamp = new Date().getTime();
      const response = await apiClient.get(`/files/individual?_t=${timestamp}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener archivos individuales:', error);
      throw error;
    }
  },

  /**
   * Adjunta archivos individuales al mensaje actual
   * @param {Array} fileIds IDs de los archivos a adjuntar
   * @returns {Promise<Object>} Respuesta con la confirmación
   */
  attachFilesToMessage: async (fileIds) => {
    try {
      const response = await apiClient.post('/messages/attach-files', { fileIds });
      return response.data;
    } catch (error) {
      console.error('Error al adjuntar archivos al mensaje:', error);
      throw error;
    }
  },

  /**
   * Obtiene los documentos editables del usuario
   * @returns {Promise<Object>} Respuesta con los documentos
   */
  getEditableDocuments: async () => {
    try {
      // Obtener archivos individuales y filtrar documentos editables
      const response = await agtEduService.getIndividualFiles();
      
      if (response.success && response.data) {
        // Filtrar documentos por extensión
        const editableExtensions = ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'pdf', 'txt', 'md'];
        const editableDocuments = response.data.filter(file => {
          const extension = file.name.split('.').pop().toLowerCase();
          return editableExtensions.includes(extension);
        });
        
        return {
          success: true,
          data: editableDocuments
        };
      }
      
      return response;
    } catch (error) {
      console.error('Error al obtener documentos editables:', error);
      throw error;
    }
  },

  /**
   * Obtiene el historial de ediciones de un documento
   * @param {string} documentId ID del documento
   * @returns {Promise<Object>} Respuesta con el historial de ediciones
   */
  getDocumentEditHistory: async (documentId) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener historial de ediciones:', error);
      
      // Si el endpoint no existe todavía, devolver un historial vacío
      return {
        success: true,
        data: []
      };
    }
  },

  /**
   * Aplica cambios a un documento
   * @param {string} documentId ID del documento
   * @param {Object} changes Objeto con los cambios a aplicar
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  applyDocumentChanges: async (documentId, changes) => {
    try {
      const response = await apiClient.post(`/documents/${documentId}/apply-changes`, { changes });
      return response.data;
    } catch (error) {
      console.error('Error al aplicar cambios al documento:', error);
      
      // Si el endpoint no existe todavía, simular una respuesta exitosa
      return {
        success: true,
        data: {
          documentId,
          changesApplied: changes.changes.length,
          timestamp: new Date().toISOString()
        }
      };
    }
  },

  /**
   * Guarda el documento modificado
   * @param {string} documentId ID del documento
   * @param {FormData} formData Datos del documento modificado
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  saveModifiedDocument: async (documentId, formData) => {
    try {
      const response = await apiClient.post(`/documents/${documentId}/save`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error al guardar documento modificado:', error);
      
      // Si el endpoint no existe todavía, simular una respuesta exitosa
      return {
        success: true,
        data: {
          documentId,
          saved: true,
          timestamp: new Date().toISOString()
        }
      };
    }
  },

  /**
   * Envía un mensaje específico para la edición de documentos
   * @param {Object} messageData Datos del mensaje
   * @param {string} messageData.text Texto del mensaje
   * @param {string} messageData.documentId ID del documento
   * @param {string} messageData.documentName Nombre del documento
   * @returns {Promise<Object>} Respuesta con los cambios propuestos por el asistente
   */
  sendDocumentEditMessage: async (messageData) => {
    try {
      // Asegurarse de que el mensaje indique claramente que es para editar un documento
      const enhancedData = {
        ...messageData,
        text: `[DOCUMENTO] ${messageData.text}`,
        isDocumentEdit: true
      };
      
      // Usar el endpoint de mensajes estándar
      const response = await apiClient.post('/messages', enhancedData);
      return response.data;
    } catch (error) {
      console.error('Error al enviar mensaje de edición de documento:', error);
      throw error;
    }
  }
};

export default agtEduService;